import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { ShopifyProduct } from "@/lib/shopify";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";

const ITEM_CLASS =
  "snap-start shrink-0 w-[44%] sm:w-[38%] md:w-[260px] lg:w-[300px] xl:w-[340px]";

export function ProductCarouselRow({
  heading,
  subheading,
  products,
  isLoading,
  children,
  scrollResetKey,
  shopAllLink,
}: {
  heading: string;
  subheading: string;
  products?: ShopifyProduct[];
  isLoading?: boolean;
  /** Optional extra content (e.g. tabs) rendered in the info stack */
  children?: ReactNode;
  /** Change this value to reset scroll position to the start */
  scrollResetKey?: string;
  shopAllLink?: { label: string; to: string; params?: Record<string, string> };
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef({ client: 0, scroll: 0 });
  const rafRef = useRef<number | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const applyState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { client, scroll } = metricsRef.current;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + client < scroll - 4);
  }, []);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    metricsRef.current = { client: el.clientWidth, scroll: el.scrollWidth };
  }, []);

  const update = useCallback(() => {
    measure();
    applyState();
  }, [measure, applyState]);

  const onScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applyState();
    });
  }, [applyState]);

  useEffect(() => {
    const onResize = () => update();
    update();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [update]);

  useEffect(() => {
    update();
  }, [update, products, isLoading]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: "auto" });
    update();
  }, [scrollResetKey, update]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const first = el.firstElementChild as HTMLElement | null;
    const step = first ? first.offsetWidth + 24 : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const arrowClass = (enabled: boolean) =>
    `text-[#2A1510] transition-colors hover:text-[#FA5021] ${enabled ? "" : "opacity-30 pointer-events-none"}`;

  const count = products?.length ?? 0;

  const renderArrows = (size: "sm" | "md") => {
    const cls = size === "sm" ? "h-5 w-5" : "h-6 w-6";
    return (
      <div className={`flex items-center ${size === "sm" ? "gap-4" : "gap-5"}`}>
        <button
          type="button"
          aria-label={`Scroll ${heading} left`}
          onClick={() => scrollBy(-1)}
          className={arrowClass(canPrev)}
        >
          <ArrowLeft className={cls} strokeWidth={1.25} />
        </button>
        <button
          type="button"
          aria-label={`Scroll ${heading} right`}
          onClick={() => scrollBy(1)}
          className={arrowClass(canNext)}
        >
          <ArrowRight className={cls} strokeWidth={1.25} />
        </button>
      </div>
    );
  };

  const renderShopAll = (variant: "rail" | "mobile") =>
    shopAllLink ? (
      <Link
        to={shopAllLink.to}
        params={shopAllLink.params as never}
        className={
          variant === "rail"
            ? "btn-cc-accent w-full whitespace-nowrap !px-3 !py-3 !text-[10px] lg:!text-[11px]"
            : "btn-cc-accent"
        }
      >
        {shopAllLink.label}
      </Link>
    ) : null;

  return (
    <div className="w-full overflow-hidden">
      <div className="mb-6 hidden justify-end pl-[max(1.5rem,calc((100%-1400px)/2+1.5rem))] pr-[max(1.5rem,calc((100%-1400px)/2+1.5rem))] md:flex">
        {renderArrows("md")}
      </div>
      <div className="pl-[max(1.5rem,calc((100%-1400px)/2+1.5rem))] md:grid md:grid-cols-[180px_minmax(0,1fr)] md:items-stretch md:gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* Rail */}
        <div className="pr-6 md:pr-0">
          {/* Mobile stacked header */}
          <div className="md:hidden">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-heading text-3xl font-bold uppercase text-[#2A1510]">{heading}</h2>
                <p className="mt-1 font-body text-sm italic text-[#8B6F47]">{subheading}</p>
              </div>
              {renderArrows("sm")}
            </div>
            {children}
            {shopAllLink && <div className="mt-4">{renderShopAll("mobile")}</div>}
          </div>

          {/* Desktop rail: spine + info stack */}
          <div className="hidden h-full md:flex">
            <div className="flex items-start pr-2">
              <h2
                className="font-heading text-2xl font-bold uppercase tracking-[0.05em] text-[#2A1510] lg:text-3xl"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                {heading}
              </h2>
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-start gap-4 pl-3 pt-1">
              <p className="max-w-[115px] font-body text-xs italic leading-snug text-[#8B6F47] lg:max-w-[150px] lg:text-sm">
                {subheading}
              </p>

              {children}

              {!isLoading && (
                <div>
                  <div className="font-heading text-3xl leading-none text-[#C6A971]">{count}</div>
                  <div className="font-ui text-[9px] uppercase tracking-[2px] text-[#9C9287]">Styles</div>
                </div>
              )}

              {renderShopAll("rail")}
            </div>
          </div>
        </div>


        {/* Card track — full bleed to the right edge */}
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="mt-6 flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pr-6 md:mt-0 md:pl-0 md:pr-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={ITEM_CLASS}>
                  <ProductCardSkeleton />
                </div>
              ))
            : products && products.length > 0
              ? products.map((product) => (
                  <div key={product.node.id} className={ITEM_CLASS}>
                    <ProductCard product={product} />
                  </div>
                ))
              : <p className="font-ui text-muted-foreground">No products found</p>}
        </div>
      </div>
    </div>
  );
}
